<template>
  <article
    class="relatorio-task-card"
    :style="{ '--rtc-accent-left': accentColor }"
    @click="$emit('click')"
  >
    <header class="rtc-header">
      <h3 class="rtc-title">{{ task.titulo }}</h3>
      <span
        v-if="task.colunaNome"
        class="rtc-coluna"
        :style="colunaPillStyle"
      >{{ task.colunaNome }}</span>
    </header>

    <p v-if="task.descricao" class="rtc-desc" :title="task.descricao">{{ task.descricao }}</p>

    <motion.div v-if="tags.length" class="rtc-tags">
      <span
        v-for="tag in tags"
        :key="tag.id"
        class="rtc-tag"
        :style="{ backgroundColor: tag.cor || '#6366f1' }"
      >
        <span class="rtc-tag-icon">{{ getTagEmoji(tag.icone) }}</span>
        <span class="rtc-tag-name" :style="{ color: getTextColor(tag.cor) }">{{ tag.nome }}</span>
      </span>
    </motion.div>

    <motion.div v-if="usuarios.length" class="rtc-users">
      <motion.div
        v-for="u in usuarios"
        :key="u.id"
        class="rtc-user"
        :title="u.nome"
      >
        <img
          v-if="avatarUrl(u)"
          :src="avatarUrl(u)"
          :alt="u.nome"
          class="rtc-user-img"
        />
        <span v-else class="rtc-user-fallback">{{ userInitials(u.nome) }}</span>
      </motion.div>
    </motion.div>

    <section v-if="task.agendamento" class="rtc-block rtc-block--schedule">
      <div class="rtc-schedule-card">
        <div class="rtc-schedule-header">
          <svg class="rtc-schedule-header-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          <span class="rtc-schedule-header-title">{{ t('workspaceHistoria.taskCard.schedule') }}</span>
        </div>
        <div class="rtc-schedule-body">
          <div class="rtc-schedule-datetime">
            <div class="rtc-schedule-chip rtc-schedule-chip--date">
              <span class="rtc-schedule-chip-icon" aria-hidden="true">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </span>
              <div class="rtc-schedule-chip-text">
                <span class="rtc-schedule-chip-lbl">{{ t('workspaceHistoria.taskCard.date') }}</span>
                <strong>{{ agendamentoDataFmt }}</strong>
                <span v-if="agendamentoWeekday" class="rtc-schedule-weekday">{{ agendamentoWeekday }}</span>
              </div>
            </div>
            <div v-if="agendamentoHoraFmt" class="rtc-schedule-chip rtc-schedule-chip--time">
              <span class="rtc-schedule-chip-icon" aria-hidden="true">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </span>
              <div class="rtc-schedule-chip-text">
                <span class="rtc-schedule-chip-lbl">{{ t('workspaceHistoria.taskCard.time') }}</span>
                <strong>{{ agendamentoHoraFmt }}</strong>
              </div>
            </div>
          </div>
          <div
            v-if="task.agendamento.motivoNome"
            class="rtc-schedule-motivo"
          >
            <span
              class="rtc-schedule-motivo-icon"
              :style="{ backgroundColor: task.agendamento.motivoCor || '#5865F2' }"
            >{{ getTagEmoji(task.agendamento.motivoIcone) }}</span>
            <div class="rtc-schedule-motivo-text">
              <span class="rtc-schedule-chip-lbl">{{ t('workspaceHistoria.taskCard.reason') }}</span>
              <span class="rtc-schedule-motivo-nome">{{ task.agendamento.motivoNome }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="checklistCats.length" class="rtc-block">
      <motion.div class="rtc-block-head">
        <span class="rtc-block-ico">☑</span>
        <span class="rtc-block-title">{{ t('workspaceHistoria.taskCard.checklist') }}</span>
        <span class="rtc-block-badge">{{ task.checklistConcluidos }}/{{ task.checklistTotal }}</span>
      </motion.div>
      <motion.div v-for="cat in checklistCats" :key="cat.nome" class="rtc-chk-cat">
        <div class="rtc-chk-top">
          <span>{{ cat.nome }}</span>
          <span class="rtc-chk-count">{{ cat.concluidos }}/{{ cat.total }}</span>
          <strong>{{ cat.percentual }}%</strong>
        </div>
        <div class="rtc-chk-bar">
          <motion.div class="rtc-chk-fill" :style="{ width: cat.percentual + '%' }" />
        </div>
      </motion.div>
    </section>

    <div v-if="hasAnexos" class="rtc-anexos-bar">
      <span class="rtc-anexos-label">{{ t('workspaceHistoria.taskCard.attachments') }}</span>
      <div class="rtc-anexos-pills">
        <span v-if="anexos.qtdImagens" class="rtc-anexo-pill">
          <svg class="rtc-anexo-ico" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          {{ anexos.qtdImagens }} {{ anexos.qtdImagens === 1 ? t('workspaceHistoria.taskCard.image') : t('workspaceHistoria.taskCard.images') }}
        </span>
        <span v-if="anexos.qtdArquivos" class="rtc-anexo-pill">
          <svg class="rtc-anexo-ico" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          {{ anexos.qtdArquivos }} {{ anexos.qtdArquivos === 1 ? t('workspaceHistoria.taskCard.file') : t('workspaceHistoria.taskCard.files') }}
        </span>
      </div>
    </div>

    <section v-if="showFinanceiro && hasFinanceiro" class="rtc-block rtc-block--finance">
      <div class="rtc-block-head">
        <span class="rtc-block-ico">💳</span>
        <span class="rtc-block-title">{{ t('workspaceHistoria.taskCard.financial') }}</span>
        <span class="rtc-fin-badge">{{ financeiro.quantidade }} {{ financeiro.quantidade === 1 ? t('workspaceHistoria.receivable') : t('workspaceHistoria.receivables') }}</span>
      </div>
      <div class="rtc-fin-dashboard">
        <article
          v-for="card in finCards"
          :key="card.key"
          class="rtc-fin-stat-card"
          :class="'rtc-fin-stat-card--' + card.key"
        >
          <div class="rtc-fin-stat-top">
            <span class="rtc-fin-stat-icon" :style="{ backgroundColor: card.corSoft }">{{ card.icon }}</span>
            <span class="rtc-fin-stat-qtd">{{ card.qtd }} {{ card.qtd === 1 ? t('workspaceHistoria.item') : t('workspaceHistoria.items') }}</span>
          </div>
          <span class="rtc-fin-stat-label">{{ card.label }}</span>
          <MoneyAmount :value="card.valor" tag="strong" class="rtc-fin-stat-value" />
          <div class="rtc-fin-stat-bar-wrap">
            <div
              class="rtc-fin-stat-bar"
              :style="{ width: card.pct + '%', backgroundColor: card.cor }"
            />
          </div>
          <span class="rtc-fin-stat-pct">{{ t('workspaceHistoria.pctOfTotal', { pct: card.pct }) }}</span>
        </article>
      </div>
    </section>

    <section v-if="showHoras && horas.length" class="rtc-block">
      <div class="rtc-block-head">
        <span class="rtc-block-ico">⏱️</span>
        <span class="rtc-block-title">{{ t('workspaceHistoria.taskCard.hoursWorked') }}</span>
      </div>
      <ul class="rtc-horas">
        <li v-for="h in horas" :key="h.usuarioId" class="rtc-hora-row">
          <span class="rtc-hora-avatar">
            <img
              v-if="avatarUrl(h)"
              :src="avatarUrl(h)"
              :alt="h.nome"
              class="rtc-hora-img"
            />
            <span v-else class="rtc-hora-fallback">{{ userInitials(h.nome) }}</span>
          </span>
          <span class="rtc-hora-nome">{{ h.nome }}</span>
          <strong class="rtc-hora-val">{{ h.totalFormatado }}</strong>
        </li>
      </ul>
    </section>

    <section class="rtc-block rtc-progress-block">
      <motion.div class="rtc-block-head">
        <span class="rtc-block-ico">📊</span>
        <span class="rtc-block-title">{{ t('workspaceHistoria.taskCard.progress') }}</span>
      </motion.div>
      <div class="rtc-progress-row">
        <div class="rtc-progress-track">
          <motion.div
            class="rtc-progress-fill"
            :class="progressBarClass"
            :style="{ width: progressPct + '%' }"
          />
        </div>
        <span class="rtc-progress-pct" :class="progressClass">{{ progressPct }}%</span>
      </div>
      <div class="rtc-prazo-row">
        <span class="rtc-fin-lbl">{{ t('workspaceHistoria.taskCard.deadline') }}</span>
        <span class="rtc-prazo-val" :class="prazoClass">
          <span class="rtc-prazo-icon">{{ prazoIcon }}</span>
          {{ formatDate(task.prazoTarefa) || t('archivedDrawer.emptyValue') }}
        </span>
      </div>
    </section>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { motion } from 'motion-v'
import api from '../services/api'
import { getTagIcon } from '../utils/iconMapper'
import MoneyAmount from './MoneyAmount.vue'

const { t, locale } = useI18n()

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const props = defineProps({
  task: { type: Object, required: true },
  workspaceUsers: { type: Array, default: () => [] },
  showFinanceiro: { type: Boolean, default: true },
  showHoras: { type: Boolean, default: true }
})

defineEmits(['click'])

const accentColor = computed(() => props.task?.cor || '#6366f1')
const colunaCor = computed(() => props.task?.colunaCor || '#6366f1')
const tags = computed(() => {
  if (props.task?.tags?.length) return props.task.tags
  return []
})
const usuarios = computed(() => props.task?.usuariosAtribuidos || [])
const horas = computed(() => props.task?.horasPorUsuario || [])
const checklistCats = computed(() => props.task?.checklistCategorias || [])
const anexos = computed(() => props.task?.anexosResumo || { qtdImagens: 0, qtdArquivos: 0 })
const financeiro = computed(() => props.task?.financeiro || {})
const hasAnexos = computed(() => (anexos.value.qtdImagens || 0) + (anexos.value.qtdArquivos || 0) > 0)
const hasFinanceiro = computed(() => (financeiro.value.quantidade || 0) > 0)

const finCards = computed(() => {
  const f = financeiro.value
  const valorPendente = Number(f.valorPendente) || 0
  const valorVencido = Number(f.valorVencido) || 0
  const valorPago = Number(f.valorRecebido) || 0
  const valorTotal = valorPendente + valorVencido + valorPago
  const pct = (v) => (valorTotal > 0 ? Math.round((v / valorTotal) * 100) : 0)
  const qtdPendente = f.qtdPendente ?? 0
  const qtdVencido = f.qtdVencido ?? 0
  const qtdPago = f.qtdPago ?? 0
  return [
    {
      key: 'pendente',
      label: t('workspaceHistoria.taskCard.pending'),
      icon: '⏳',
      qtd: qtdPendente,
      valor: valorPendente,
      cor: '#6366f1',
      corSoft: 'rgba(99, 102, 241, 0.15)',
      pct: pct(valorPendente)
    },
    {
      key: 'vencido',
      label: t('workspaceHistoria.taskCard.overdue'),
      icon: '⚠️',
      qtd: qtdVencido,
      valor: valorVencido,
      cor: '#ef4444',
      corSoft: 'rgba(239, 68, 68, 0.15)',
      pct: pct(valorVencido)
    },
    {
      key: 'pago',
      label: t('workspaceHistoria.taskCard.paid'),
      icon: '✓',
      qtd: qtdPago,
      valor: valorPago,
      cor: '#10b981',
      corSoft: 'rgba(16, 185, 129, 0.15)',
      pct: pct(valorPago)
    }
  ]
})

const progressPct = computed(() => Math.min(100, Math.max(0, props.task?.progressoTarefaPercentual ?? 0)))

const resolveAvatarUrl = (url) => {
  if (!url) return null
  const clean = String(url).trim()
  if (!clean) return null
  if (/^https?:\/\//i.test(clean) || clean.startsWith('data:')) return clean
  if (clean.startsWith('/')) {
    const base = (api.defaults.baseURL || '').replace(/\/+$/, '')
    return `${base}${clean}`
  }
  return clean
}

const fotoFromWorkspace = (usuarioId) => {
  const u = props.workspaceUsers.find((x) => Number(x.id) === Number(usuarioId))
  return u?.fotoPerfil ?? null
}

const avatarUrl = (u) => {
  const foto = u?.fotoPerfil || fotoFromWorkspace(u?.usuarioId ?? u?.id)
  return resolveAvatarUrl(foto)
}

const userInitials = (nome) => {
  if (!nome) return '?'
  const parts = String(nome).trim().split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const getTagEmoji = (iconValue) => getTagIcon(iconValue)

const isLightColor = (hex) => {
  if (!hex || typeof hex !== 'string') return true
  const c = hex.replace('#', '')
  if (c.length < 6) return true
  const r = parseInt(c.slice(0, 2), 16)
  const g = parseInt(c.slice(2, 4), 16)
  const b = parseInt(c.slice(4, 6), 16)
  return ((r * 299) + (g * 587) + (b * 114)) / 1000 > 128
}

const getTextColor = (backgroundColor) => (isLightColor(backgroundColor) ? '#000000' : '#FFFFFF')

const colunaPillStyle = computed(() => {
  const bg = colunaCor.value
  return {
    backgroundColor: bg,
    color: getTextColor(bg)
  }
})

const agendamento = computed(() => props.task?.agendamento)

function parseAgendamentoDate(value) {
  if (value == null) return null
  if (Array.isArray(value) && value.length >= 3) {
    const [y, m, d] = value
    return new Date(Number(y), Number(m) - 1, Number(d))
  }
  if (typeof value === 'object' && !(value instanceof Date)) {
    if (value.year != null && value.day != null) {
      const mo = value.monthValue ?? value.month ?? 1
      return new Date(value.year, Number(mo) - 1, value.day)
    }
  }
  const s = String(value).trim()
  const csv = s.match(/^(\d{4})\s*,\s*(\d{1,2})\s*,\s*(\d{1,2})/)
  if (csv) {
    return new Date(Number(csv[1]), Number(csv[2]) - 1, Number(csv[3]))
  }
  const iso = s.split('T')[0]
  const parts = iso.split('-')
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10)
    const m = parseInt(parts[1], 10)
    const d = parseInt(parts[2], 10)
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return new Date(y, m - 1, d)
    }
  }
  const dt = new Date(s)
  return Number.isNaN(dt.getTime()) ? null : dt
}

const formatDate = (value) => {
  const dt = parseAgendamentoDate(value)
  if (!dt) return value ? String(value) : ''
  const d = String(dt.getDate()).padStart(2, '0')
  const m = String(dt.getMonth() + 1).padStart(2, '0')
  const y = dt.getFullYear()
  return `${d}/${m}/${y}`
}

const formatHorario = (value) => {
  if (!value) return ''
  const match = String(value).match(/^(\d{1,2}):(\d{2})/)
  if (match) return `${match[1].padStart(2, '0')}:${match[2]}`
  return String(value)
}

const agendamentoDataFmt = computed(() => formatDate(agendamento.value?.dataAgendamento))
const agendamentoHoraFmt = computed(() => formatHorario(agendamento.value?.horario))
const agendamentoWeekday = computed(() => {
  const dt = parseAgendamentoDate(agendamento.value?.dataAgendamento)
  if (!dt) return ''
  const wd = dt.toLocaleDateString(dateLocale.value, { weekday: 'long' })
  return wd.charAt(0).toUpperCase() + wd.slice(1)
})

const progressClass = computed(() => {
  const pct = progressPct.value
  if (pct >= 80) return 'progress-high'
  if (pct >= 50) return 'progress-medium'
  return 'progress-low'
})

const progressBarClass = computed(() => {
  const pct = progressPct.value
  if (pct >= 80) return 'bar-high'
  if (pct >= 50) return 'bar-medium'
  return 'bar-low'
})

const prazoStatus = computed(() => {
  const prazo = props.task?.prazoTarefa
  if (!prazo) return 'neutral'
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const dataPrazo = new Date(String(prazo).split('T')[0] + 'T00:00:00')
  if (Number.isNaN(dataPrazo.getTime())) return 'neutral'
  const diffDias = Math.ceil((dataPrazo.getTime() - hoje.getTime()) / 86400000)
  if (diffDias < 0) return 'overdue'
  if (diffDias <= 5) return 'near'
  return 'ok'
})

const prazoIcon = computed(() => {
  if (prazoStatus.value === 'overdue') return '⚠'
  if (prazoStatus.value === 'near') return '⏳'
  if (prazoStatus.value === 'ok') return '📅'
  return '•'
})

const prazoClass = computed(() => {
  if (prazoStatus.value === 'overdue') return 'prazo-overdue'
  if (prazoStatus.value === 'near') return 'prazo-near'
  if (prazoStatus.value === 'ok') return 'prazo-ok'
  return 'prazo-neutral'
})
</script>

<style scoped>
.relatorio-task-card {
  width: 100%;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-left-width: 3px;
  border-left-color: var(--rtc-accent-left, var(--border-primary));
  border-radius: 4px;
  padding: 20px 22px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}
.relatorio-task-card:hover {
  border-color: var(--border-hover);
  border-left-color: var(--border-hover);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}
.rtc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.rtc-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--text-primary);
  flex: 1;
}
.rtc-coluna {
  font-size: 11px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 4px;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.rtc-desc {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.rtc-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.rtc-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}
.rtc-tag-icon { font-size: 12px; }
.rtc-users {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}
.rtc-user {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid var(--bg-primary);
  box-shadow: 0 0 0 1px var(--border-primary);
}
.rtc-user-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.rtc-user-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
}
.rtc-block {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-primary);
}
.rtc-block-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.rtc-block-ico { font-size: 14px; }
.rtc-block-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-secondary);
}
.rtc-block-badge {
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
}
.rtc-block--schedule {
  border-top: none;
  margin-top: 14px;
  padding-top: 0;
}
.rtc-schedule-card {
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  background: linear-gradient(135deg, var(--bg-secondary) 0%, rgba(99, 102, 241, 0.05) 100%);
}
.rtc-schedule-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-primary);
}
.rtc-schedule-header-svg {
  width: 18px;
  height: 18px;
  color: #6366f1;
  flex-shrink: 0;
}
.rtc-schedule-header-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
}
.rtc-schedule-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.rtc-schedule-datetime {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
@media (max-width: 520px) {
  .rtc-schedule-datetime { grid-template-columns: 1fr; }
}
.rtc-schedule-chip {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 4px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
}
.rtc-schedule-chip--time {
  border-color: rgba(139, 92, 246, 0.25);
  background: linear-gradient(135deg, var(--bg-primary), rgba(139, 92, 246, 0.04));
}
.rtc-schedule-chip--date {
  border-color: rgba(99, 102, 241, 0.25);
  background: linear-gradient(135deg, var(--bg-primary), rgba(99, 102, 241, 0.04));
}
.rtc-schedule-chip-icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(99, 102, 241, 0.12);
  color: #6366f1;
}
.rtc-schedule-chip--time .rtc-schedule-chip-icon {
  background: rgba(139, 92, 246, 0.12);
  color: #8b5cf6;
}
.rtc-schedule-chip-icon svg {
  width: 16px;
  height: 16px;
}
.rtc-schedule-chip-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rtc-schedule-chip-lbl {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-secondary);
}
.rtc-schedule-chip-text strong {
  font-size: 15px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
}
.rtc-schedule-weekday {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  text-transform: capitalize;
}
.rtc-schedule-motivo {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 4px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
}
.rtc-schedule-motivo-icon {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.rtc-schedule-motivo-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rtc-schedule-motivo-nome {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}
.rtc-chk-cat { margin-bottom: 10px; }
.rtc-chk-cat:last-child { margin-bottom: 0; }
.rtc-chk-top {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  margin-bottom: 6px;
}
.rtc-chk-top span:first-child { flex: 1; font-weight: 600; }
.rtc-chk-count { color: var(--text-secondary); }
.rtc-chk-bar {
  height: 6px;
  border-radius: 999px;
  background: var(--bg-secondary);
  overflow: hidden;
}
.rtc-chk-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}
.rtc-anexos-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
}
.rtc-anexos-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-secondary);
  flex-shrink: 0;
}
.rtc-anexos-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.rtc-anexo-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
}
.rtc-anexo-ico {
  width: 14px;
  height: 14px;
  opacity: 0.7;
}
.rtc-block--finance {
  padding: 16px;
  margin-top: 14px;
  border-radius: 4px;
  background: linear-gradient(135deg, var(--bg-secondary) 0%, rgba(99, 102, 241, 0.04) 100%);
  border: 1px solid var(--border-primary);
  border-top: 1px solid var(--border-primary);
}
.rtc-fin-badge {
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--bg-primary);
  color: var(--text-secondary);
}
.rtc-fin-dashboard {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
@media (max-width: 720px) {
  .rtc-fin-dashboard { grid-template-columns: 1fr; }
}
.rtc-fin-stat-card {
  padding: 14px 16px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.rtc-fin-stat-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
}
.rtc-fin-stat-icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.rtc-fin-stat-qtd {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--bg-secondary);
}
.rtc-fin-stat-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-secondary);
}
.rtc-fin-stat-value {
  font-size: 18px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
}
.rtc-fin-stat-bar-wrap {
  height: 5px;
  background: var(--bg-secondary);
  border-radius: 999px;
  overflow: hidden;
  margin-top: 2px;
}
.rtc-fin-stat-bar {
  height: 100%;
  border-radius: 999px;
  min-width: 3px;
  transition: width 0.35s ease;
}
.rtc-fin-stat-pct {
  font-size: 10px;
  color: var(--text-secondary);
}
.rtc-fin-lbl {
  font-size: 11px;
  color: var(--text-secondary);
  font-weight: 500;
}
.rtc-horas {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rtc-hora-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.rtc-hora-avatar {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}
.rtc-hora-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.rtc-hora-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
}
.rtc-hora-nome {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}
.rtc-hora-val {
  font-size: 13px;
  color: #6366f1;
}
.rtc-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.rtc-progress-track {
  flex: 1;
  height: 8px;
  border-radius: 999px;
  background: var(--bg-secondary);
  overflow: hidden;
}
.rtc-progress-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.35s ease;
}
.rtc-progress-fill.bar-low { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.rtc-progress-fill.bar-medium { background: linear-gradient(90deg, #6366f1, #8b5cf6); }
.rtc-progress-fill.bar-high { background: linear-gradient(90deg, #10b981, #34d399); }
.rtc-progress-pct {
  font-size: 13px;
  font-weight: 700;
  min-width: 40px;
  text-align: right;
}
.rtc-progress-pct.progress-low { color: #f59e0b; }
.rtc-progress-pct.progress-medium { color: #6366f1; }
.rtc-progress-pct.progress-high { color: #10b981; }
.rtc-prazo-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.rtc-prazo-val {
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}
.rtc-prazo-val.prazo-overdue { color: #ef4444; }
.rtc-prazo-val.prazo-near { color: #f59e0b; }
.rtc-prazo-val.prazo-ok { color: var(--text-primary); }
.rtc-prazo-val.prazo-neutral { color: var(--text-secondary); }
</style>
